import { RefObject, useRef, useState } from 'react';

import { EditableTemplate, TemplateBuilderDialog } from '../template-builder/template-builder-dialog';
import { TemplatesPopover } from '../templates/templates-popover';
import { KIM_FILE_INPUT_ACCEPT } from '../utils/upload-constants';
import { DocMentionsInputHandle } from '@/new/components/doc-mentions-input';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';

interface KimActionsMenuProps {
  /** Ref to the text input, used to trigger an @ mention. */
  inputRef: RefObject<DocMentionsInputHandle>;
  onFilesAdded: (files: File[]) => void;
  onTemplateSelect: (body: string) => void;
  disabled?: boolean;
  isUploading?: boolean;
}

/**
 * The bottom-left "+" menu for the Kim composer.
 */
export function KimActionsMenu({
  inputRef,
  onFilesAdded,
  onTemplateSelect,
  disabled,
  isUploading,
}: KimActionsMenuProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [isTemplateBuilderOpen, setIsTemplateBuilderOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EditableTemplate | null>(null);

  // Mention and Templates must run once the menu has closed — while it's open its focus trap
  // reverts focus (breaking triggerMention's execCommand) and the closing click would be seen as
  // an outside-interaction that instantly dismisses the templates popover. We stash the action and
  // run it from onCloseAutoFocus, after the trap releases.
  const pendingActionRef = useRef<(() => void) | null>(null);

  return (
    <div className="relative">
      {/* Anchors the templates popover to the "+" button's box (the only laid-out child). */}
      <TemplatesPopover
        open={templatesOpen}
        onOpenChange={setTemplatesOpen}
        anchor={<span className="pointer-events-none absolute inset-0" aria-hidden />}
        side="top"
        align="start"
        onSelect={(body) => {
          onTemplateSelect(body);
          setTemplatesOpen(false);
          inputRef.current?.focus();
        }}
        onOpenTemplateBuilder={() => setIsTemplateBuilderOpen(true)}
        onEditTemplate={setEditingTemplate}
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            disabled={disabled}
            aria-label="Add to message"
            className="mt-2 ml-1 size-6.5 rounded-full"
          >
            <Icon className="lucide--plus" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          side="top"
          align="start"
          className="min-w-44"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            const action = pendingActionRef.current;
            pendingActionRef.current = null;
            action?.();
          }}
        >
          <DropdownMenuItem onSelect={() => (pendingActionRef.current = () => inputRef.current?.triggerMention())}>
            <Icon className="lucide--at-sign" /> Mention
          </DropdownMenuItem>

          <DropdownMenuItem disabled={isUploading} onSelect={() => fileInputRef.current?.click()}>
            <Icon className="lucide--paperclip" /> Add files
          </DropdownMenuItem>

          <DropdownMenuItem onSelect={() => (pendingActionRef.current = () => setTemplatesOpen(true))}>
            <Icon className="lucide--layout-template" /> Templates
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={KIM_FILE_INPUT_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length > 0) onFilesAdded(files);
          e.target.value = '';
        }}
      />

      <TemplateBuilderDialog
        open={isTemplateBuilderOpen || editingTemplate !== null}
        onOpenChange={(open) => {
          if (!open) {
            setIsTemplateBuilderOpen(false);
            setEditingTemplate(null);
          } else {
            setIsTemplateBuilderOpen(true);
          }
        }}
        template={editingTemplate ?? undefined}
      />
    </div>
  );
}
